/**
 * Google Analytics tracking utilities
 *
 * Custom events are pushed to the GTM data layer (see public/js/analytics.js).
 * Configure GA4 Event tags in GTM with Custom Event triggers matching `event`.
 */

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Track a custom event in Google Analytics via GTM
 * @param eventName - Custom event name (GTM trigger: Custom Event)
 * @param eventParams - Optional parameters (map to GA4 event params in GTM)
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>
): void {
  try {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...(eventParams || {}),
    });
  } catch (error) {}
}

/**
 * Track user sign up event
 * @param method - Sign up method ('email', 'google', or 'microsoft')
 */
export function trackSignUp(method: 'email' | 'google' | 'microsoft'): void {
  trackEvent('sign_up', {
    method: method,
  });
}

/**
 * Track user login event
 * @param method - Login method ('email', 'google', or 'microsoft')
 */
export function trackLogin(method: 'email' | 'google' | 'microsoft'): void {
  trackEvent('login', {
    method: method,
  });
}

/**
 * Track first template created event
 * This should be called when a user creates their first template
 * @param userId - The user's ID to make tracking user-specific
 */
export function trackFirstTemplateCreated(userId: string | null): void {
  if (!userId) return; // Don't track if no user ID

  // Check if this is the user's first template (user-specific)
  const firstTemplateKey = `layerre_first_template_created_tracked_${userId}`;
  const hasTrackedFirstTemplate = localStorage.getItem(firstTemplateKey);

  if (!hasTrackedFirstTemplate) {
    trackEvent('first_template_created');
    // Mark that we've tracked the first template for this user
    localStorage.setItem(firstTemplateKey, 'true');
  }
}

export function trackOnboardingStarted(): void {
  trackEvent('onboarding_started');
}

export function trackOnboardingStepViewed(
  stepId: string,
  stepIndex: number
): void {
  trackEvent('onboarding_step_viewed', {
    step_id: stepId,
    step_index: stepIndex,
  });
}

export function trackOnboardingSkipped(stepId?: string): void {
  trackEvent('onboarding_skipped', stepId ? { step_id: stepId } : undefined);
}

export function trackOnboardingCompleted(): void {
  trackEvent('onboarding_completed');
}
